import { useEffect, useRef } from 'react'
import { useSessionUID } from 'src/session/useSession'
import { advancedPost } from '../../api/client'
import { CareTeamChatMessage } from './types'

const readPath = (uid: string, threadId: string | null) =>
  threadId
    ? `/patients/${encodeURIComponent(uid)}/care-team-chat/threads/${encodeURIComponent(threadId)}/read`
    : `/patients/${encodeURIComponent(uid)}/care-team-chat/read`

// Marks through the newest message from someone else, so one that lands mid-send stays unread.
export const useMarkCareTeamChatRead = (
  uid: string,
  threadId: string | null,
  messages: CareTeamChatMessage[],
  onRead: () => void
) => {
  const coachUid = useSessionUID()
  const marked = useRef<string>()
  const newestFromOthers = messages.reduce<string | undefined>(
    (newest, message) => (message.authorCoachId === coachUid ? newest : message.id),
    undefined
  )

  useEffect(() => {
    if (!newestFromOthers) {
      return
    }
    const key = `${uid} ${threadId ?? ''} ${newestFromOthers}`
    if (marked.current === key) {
      return
    }
    marked.current = key

    advancedPost(readPath(uid, threadId), { messageId: newestFromOthers })
      .then(onRead)
      .catch(() => {
        // Stale: a newer attempt already superseded this one, so clearing it here would retry the wrong key.
        if (marked.current === key) {
          marked.current = undefined
        }
      })
  }, [uid, threadId, newestFromOthers, onRead])
}
